import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  RoleDashboardKpis,
} from '@/models/dashboard-kpi.model'
import { UserRole } from '@/models/user.model'
import { kpiService } from '@/services/kpiService'

interface UseDashboardKpisResult {
  role: UserRole | null
  data: RoleDashboardKpis | null
  loading: boolean
  error: string | null
  refreshing: boolean
  refresh: () => Promise<void>
}

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Failed to load dashboard KPIs.'

export const useDashboardKpis = (): UseDashboardKpisResult => {
  const { user } = useAuth()
  const role = useMemo(() => user?.role ?? null, [user?.role])

  const [data, setData] = useState<RoleDashboardKpis | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [refreshing, setRefreshing] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const fetchKpis = useCallback(async () => {
    if (!role) {
      throw new Error('Missing user role. Please sign in again.')
    }
    return kpiService.getRoleKpis(role)
  }, [role])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (!role) {
        setData(null)
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const result = await fetchKpis()
        if (!cancelled) {
          setData(result)
        }
      } catch (err) {
        if (!cancelled) {
          setError(getErrorMessage(err))
          setData(null)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [fetchKpis, role])

  const refresh = useCallback(async () => {
    if (!role) {
      setError('Missing user role. Please sign in again.')
      setData(null)
      return
    }

    setRefreshing(true)
    setError(null)

    try {
      const result = await fetchKpis()
      setData(result)
    } catch (err) {
      setError(getErrorMessage(err))
      throw err
    } finally {
      setRefreshing(false)
    }
  }, [fetchKpis, role])

  return {
    role,
    data,
    loading,
    error,
    refreshing,
    refresh,
  }
}
